import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';

// ─── Constants ─────────────────────────────────────────────────────────────────
const EMP_CATEGORIES = [
  { value:'bug',             label:'Bug Report',       icon:'bi-bug',          color:'danger'  },
  { value:'feature_request', label:'Feature Request',  icon:'bi-lightbulb',    color:'warning' },
  { value:'ui_issue',        label:'UI Issue',         icon:'bi-layout-text',  color:'info'    },
  { value:'performance',     label:'Performance',      icon:'bi-speedometer2', color:'primary' },
  { value:'general',         label:'General',          icon:'bi-chat-square',  color:'secondary'},
];
const STU_CATEGORIES = [
  { value:'class_session', label:'Class Session',   icon:'bi-person-video3', color:'primary'  },
  { value:'faculty',       label:'Faculty',         icon:'bi-person-badge',  color:'info'     },
  { value:'content',       label:'Content',         icon:'bi-book',          color:'success'  },
  { value:'infrastructure',label:'Infrastructure',  icon:'bi-building',      color:'warning'  },
  { value:'general',       label:'General',         icon:'bi-chat-square',   color:'secondary'},
];
const PRIORITIES  = ['low','medium','high','critical'];
const SEVERITIES  = ['minor','moderate','major','critical'];
const STATUSES    = [
  { value:'open',      label:'Open',       color:'danger'  },
  { value:'in_review', label:'In Review',  color:'warning' },
  { value:'resolved',  label:'Resolved',   color:'success' },
  { value:'closed',    label:'Closed',     color:'secondary'},
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const statusBadge = (s) => STATUSES.find(x => x.value === s) || { color:'secondary', label: s };
const StarRating = ({ value, onChange, readonly=false }) => (
  <div className="d-flex gap-1">
    {[1,2,3,4,5].map(n => (
      <i key={n} className={`bi ${n <= (value||0) ? 'bi-star-fill text-warning' : 'bi-star text-muted'} ${!readonly ? 'cursor-pointer' : ''}`}
        style={{fontSize:'1.1rem'}} onClick={!readonly ? () => onChange(n) : undefined}/>
    ))}
  </div>
);

// ─── BSModal ──────────────────────────────────────────────────────────────────
const BSModal = ({ show, title, size='', onClose, children, footer }) => {
  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.45)', zIndex:1050 }}>
      <div className={`modal-dialog modal-dialog-scrollable${size ? ` modal-${size}` : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose}/>
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

// ─── Submit Form ──────────────────────────────────────────────────────────────
const SubmitForm = ({ userRole, onSubmitted }) => {
  const isStudent = userRole === 'student';
  // Parents and other non-staff non-student roles only ever submit
  // employee-style feedback (bug / feature request / UI / general). The
  // student-feedback branch is K-12-specific (class session, teaching
  // quality, content clarity ratings), which doesn't apply to a parent
  // submitting feedback about the platform itself.
  const isParent = userRole === 'parent';
  const showTypeToggle = !isStudent && !isParent;
  const [feedbackType, setFeedbackType] = useState(isStudent ? 'student' : 'employee');
  const [category,     setCategory]     = useState('general');
  const [priority,     setPriority]     = useState('medium');
  const [severity,     setSeverity]     = useState('minor');
  const [sessionDate,  setSessionDate]  = useState('');
  const [sessionRating, setSessionRating] = useState(0);
  const [teachingQ,    setTeachingQ]    = useState(0);
  const [contentC,     setContentC]     = useState(0);
  const [title,        setTitle]        = useState('');
  const [message,      setMessage]      = useState('');
  const [saving,       setSaving]       = useState(false);
  const [err,          setErr]          = useState('');

  const cats = feedbackType === 'student' ? STU_CATEGORIES : EMP_CATEGORIES;
  const catKey = feedbackType === 'student' ? 'studentCategory' : 'category';

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) { setErr('Title and message are required.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        feedbackType, title, message,
        [catKey]: category,
      };
      if (feedbackType === 'employee') { payload.priority = priority; payload.severity = severity; }
      if (feedbackType === 'student') {
        payload.sessionDate = sessionDate || undefined;
        payload.sessionRating = sessionRating || undefined;
        payload.teachingQuality = teachingQ || undefined;
        payload.contentClarity = contentC || undefined;
      }
      await api.post('/training/feedback', payload);
      onSubmitted();
      setTitle(''); setMessage(''); setCategory('general'); setSessionRating(0); setTeachingQ(0); setContentC(0);
    } catch(e) { setErr(e.response?.data?.message || 'Failed to submit.'); }
    setSaving(false);
  };

  return (
    <div className="card">
      <div className="card-header py-2 fw-semibold">Submit Feedback</div>
      <div className="card-body">
        {err && <div className="alert alert-danger py-2 mb-3">{err}</div>}

        {showTypeToggle && (
          <div className="mb-3">
            <label className="form-label small fw-semibold">Feedback Type</label>
            <div className="d-flex gap-2">
              {['employee','student'].map(t => (
                <button key={t} className={`btn btn-sm ${feedbackType===t ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setFeedbackType(t); setCategory('general'); }}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category */}
        <div className="mb-3">
          <label className="form-label small fw-semibold">Category</label>
          <div className="d-flex flex-wrap gap-2">
            {cats.map(c => (
              <div key={c.value}
                className={`p-2 rounded border d-flex align-items-center gap-2 cursor-pointer ${category===c.value ? `border-${c.color} bg-${c.color} bg-opacity-10` : ''}`}
                style={{cursor:'pointer'}}
                onClick={() => setCategory(c.value)}>
                <i className={`bi ${c.icon} text-${c.color}`}/>
                <span className="small">{c.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Employee-specific */}
        {feedbackType === 'employee' && (
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Priority</label>
              <div className="d-flex gap-1 flex-wrap">
                {PRIORITIES.map(p => (
                  <button key={p} className={`btn btn-sm ${priority===p ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setPriority(p)}>{p}</button>
                ))}
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Severity</label>
              <div className="d-flex gap-1 flex-wrap">
                {SEVERITIES.map(s => (
                  <button key={s} className={`btn btn-sm ${severity===s ? 'btn-warning' : 'btn-outline-secondary'}`}
                    onClick={() => setSeverity(s)}>{s}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Student-specific */}
        {feedbackType === 'student' && (
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Session Date</label>
              <input className="form-control form-control-sm" type="date" value={sessionDate} onChange={e => setSessionDate(e.target.value)}/>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold">Overall Rating</label>
              <StarRating value={sessionRating} onChange={setSessionRating}/>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-semibold">Teaching</label>
              <StarRating value={teachingQ} onChange={setTeachingQ}/>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-semibold">Content</label>
              <StarRating value={contentC} onChange={setContentC}/>
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label small fw-semibold">Title <span className="text-danger">*</span></label>
          <input className="form-control form-control-sm" value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief title for your feedback"/>
        </div>
        <div className="mb-3">
          <label className="form-label small fw-semibold">Message <span className="text-danger">*</span></label>
          <textarea className="form-control" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your feedback in detail..."/>
        </div>
        <div className="d-flex justify-content-end">
          <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-1"/>Submitting…</> : <><i className="bi bi-send me-1"/>Submit Feedback</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Feedback Card ─────────────────────────────────────────────────────────────
const FeedbackCard = ({ item, canAdmin, onRespond, onStatusChange }) => {
  const sb = statusBadge(item.status);
  const cats = item.feedbackType === 'student' ? STU_CATEGORIES : EMP_CATEGORIES;
  const catKey = item.feedbackType === 'student' ? item.studentCategory : item.category;
  const catInfo = cats.find(c => c.value === catKey) || { color:'secondary', icon:'bi-chat-square' };

  return (
    <div className="card mb-2">
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <i className={`bi ${catInfo.icon} text-${catInfo.color}`}/>
            <span className="fw-semibold small">{item.title}</span>
            <span className={`badge bg-${catInfo.color}`}>{catKey}</span>
            <span className={`badge bg-${sb.color}`}>{sb.label}</span>
            {item.priority && <span className="badge bg-light text-dark border small">P:{item.priority}</span>}
            {item.sessionRating > 0 && (
              <span className="d-flex align-items-center gap-1 small">
                <i className="bi bi-star-fill text-warning"/>{item.sessionRating}/5
              </span>
            )}
          </div>
          <div className="d-flex gap-1 align-items-center">
            <small className="text-muted">{fmtDate(item.createdAt)}</small>
            {canAdmin && item.status !== 'closed' && (
              <>
                <button className="btn btn-outline-primary btn-sm" onClick={() => onRespond(item)} title="Respond">
                  <i className="bi bi-reply"/>
                </button>
                <select className="form-select form-select-sm" style={{width:'auto'}} value={item.status}
                  onChange={e => onStatusChange(item, e.target.value)}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </>
            )}
          </div>
        </div>
        <p className="mb-1 mt-1 small text-muted">{item.message}</p>
        {/* Submitter name. The row template is shared between the admin's
            list (which always populates submittedBy) and the user's "My
            Feedback" tab. If somehow the populate didn't resolve (e.g. the
            user document was deleted or stale data prior to the populate
            fix), fall back to the cached `submitterRole` so the row never
            renders a stranded "By:" with no value. */}
        {(item.submittedBy?.firstName || item.submitterRole) && (
          <div className="small text-muted">
            By:{' '}
            {item.submittedBy?.firstName
              ? `${item.submittedBy.firstName} ${item.submittedBy.lastName || ''}`.trim()
              : (item.submitterRole || 'User').replace(/_/g, ' ')}
            {item.submittedBy?.roleKey && (
              <span className="text-muted ms-1" style={{ fontSize: 11 }}>· {item.submittedBy.roleKey.replace(/_/g, ' ')}</span>
            )}
          </div>
        )}
        {item.response && (
          <div className="border-start border-primary ps-2 mt-2">
            <small className="text-muted fw-semibold">Admin Response:</small>
            <p className="small mb-0">{item.response}</p>
            <small className="text-muted">{item.respondedBy?.firstName} {item.respondedBy?.lastName} · {fmtDate(item.respondedAt)}</small>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function FeedbackPage() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canAdmin = ['admin','manager'].includes(role);

  const [activeTab, setActiveTab] = useState('submit');
  const [myItems,   setMyItems]   = useState([]);
  const [allItems,  setAllItems]  = useState([]);
  const [myPage,    setMyPage]    = useState(1);
  const [allPage,   setAllPage]   = useState(1);
  const [myTotal,   setMyTotal]   = useState(0);
  const [allTotal,  setAllTotal]  = useState(0);
  const [myLoading, setMyLoading] = useState(false);
  const [allLoading,setAllLoading]= useState(false);
  const [respondItem, setRespondItem] = useState(null);
  const [respText,    setRespText]    = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [alert, setAlert] = useState({ show:false, msg:'', type:'success' });

  const showAlert = (msg, type='success') => setAlert({ show:true, msg, type });

  const fetchMy = useCallback(async () => {
    setMyLoading(true);
    try {
      const r = await api.get('/training/feedback/my', { params: { page: myPage, limit: 20 } });
      setMyItems(r.data?.records || []);
      setMyTotal(r.data?.total || 0);
    } catch(_) {}
    setMyLoading(false);
  }, [myPage]);

  const fetchAll = useCallback(async () => {
    if (!canAdmin) return;
    setAllLoading(true);
    try {
      const params = { page: allPage, limit: 20 };
      if (filterStatus) params.status = filterStatus;
      const r = await api.get('/training/feedback', { params });
      setAllItems(r.data?.records || []);
      setAllTotal(r.data?.total || 0);
    } catch(_) {}
    setAllLoading(false);
  }, [canAdmin, allPage, filterStatus]);

  useEffect(() => { if (activeTab === 'my') fetchMy(); }, [activeTab, fetchMy]);
  useEffect(() => { if (activeTab === 'all') fetchAll(); }, [activeTab, fetchAll]);

  const handleRespond = async () => {
    if (!respText.trim()) return;
    try {
      await api.patch(`/training/feedback/${respondItem._id}/respond`, { response: respText });
      showAlert('Response submitted.'); setRespondItem(null); setRespText(''); fetchAll();
    } catch(e) { showAlert(e.response?.data?.message || 'Failed.', 'danger'); }
  };

  const handleStatusChange = async (item, status) => {
    try {
      await api.patch(`/training/feedback/${item._id}/status`, { status });
      fetchAll();
    } catch(_) {}
  };

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={() => setAlert(p => ({ ...p, show:false }))}/>

      <PageHeader title="Feedback" icon="bi-chat-heart" subtitle="Submit and manage feedback"/>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${activeTab==='submit'?'active':''}`} onClick={() => setActiveTab('submit')}>
          <i className="bi bi-pencil-square me-1"/>Submit
        </button></li>
        <li className="nav-item"><button className={`nav-link ${activeTab==='my'?'active':''}`} onClick={() => setActiveTab('my')}>
          <i className="bi bi-person me-1"/>My Feedback
        </button></li>
        {canAdmin && <li className="nav-item"><button className={`nav-link ${activeTab==='all'?'active':''}`} onClick={() => setActiveTab('all')}>
          <i className="bi bi-list-check me-1"/>All Feedback
        </button></li>}
      </ul>

      {activeTab === 'submit' && (
        <SubmitForm userRole={role} onSubmitted={() => { showAlert('Feedback submitted successfully!'); setActiveTab('my'); fetchMy(); }}/>
      )}

      {activeTab === 'my' && (
        <div className="card">
          <div className="card-body">
            {myLoading ? (
              <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
            ) : myItems.length === 0 ? (
              <div className="text-center py-5 text-muted"><i className="bi bi-chat-heart fs-2 d-block mb-2"/>No feedback submitted yet</div>
            ) : (
              myItems.map(item => <FeedbackCard key={item._id} item={item} canAdmin={false} onRespond={() => {}} onStatusChange={() => {}}/>)
            )}
            {myTotal > 20 && (
              <nav className="mt-3 d-flex justify-content-center">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${myPage<=1?'disabled':''}`}><button className="page-link" onClick={() => setMyPage(p => Math.max(1,p-1))}>‹</button></li>
                  <li className="page-item active"><button className="page-link">{myPage}</button></li>
                  <li className={`page-item ${myItems.length < 20?'disabled':''}`}><button className="page-link" onClick={() => setMyPage(p => p+1)}>›</button></li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      )}

      {activeTab === 'all' && canAdmin && (
        <div className="card">
          <div className="card-header py-2 d-flex gap-2 align-items-center flex-wrap">
            <span className="fw-semibold">All Feedback ({allTotal})</span>
            <select className="form-select form-select-sm ms-auto" style={{width:'auto'}} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setAllPage(1); }}>
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchAll}><i className="bi bi-arrow-clockwise"/></button>
          </div>
          <div className="card-body">
            {allLoading ? (
              <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
            ) : allItems.length === 0 ? (
              <div className="text-center py-5 text-muted"><i className="bi bi-inbox fs-2 d-block mb-2"/>No feedback found</div>
            ) : (
              allItems.map(item => (
                <FeedbackCard key={item._id} item={item} canAdmin={canAdmin}
                  onRespond={(it) => { setRespondItem(it); setRespText(it.response || ''); }}
                  onStatusChange={handleStatusChange}/>
              ))
            )}
          </div>
        </div>
      )}

      <BSModal show={!!respondItem} title="Respond to Feedback" onClose={() => { setRespondItem(null); setRespText(''); }}
        footer={<>
          <button className="btn btn-secondary btn-sm" onClick={() => { setRespondItem(null); setRespText(''); }}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleRespond}>Submit Response</button>
        </>}>
        {respondItem && (
          <>
            <p className="fw-semibold mb-1">{respondItem.title}</p>
            <p className="small text-muted mb-3">{respondItem.message}</p>
            <label className="form-label">Your Response</label>
            <textarea className="form-control" rows={4} value={respText} onChange={e => setRespText(e.target.value)} placeholder="Write your response..."/>
          </>
        )}
      </BSModal>
    </div>
  );
}
